import React, {CSSProperties} from 'react';
import {theme} from './Common';

import {IconBaseProps, IconType} from 'react-icons';
import {AiFillInfoCircle} from 'react-icons/ai';
import {BiChevronDownCircle, BiChevronUpCircle, BiMenu, BiUndo} from 'react-icons/bi';
import {BsCheckLg} from 'react-icons/bs';
import {
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaExternalLinkAlt,
  FaChevronUp,
  FaFacebookF,
  FaInstagram,
  FaPinterest,
  FaPlay,
  FaRegCalendarAlt,
  FaTwitter,
  FaYelp,
} from 'react-icons/fa';
import {FiArrowDown, FiArrowUp, FiTool} from 'react-icons/fi';
import {IoWarning, IoCloseOutline} from 'react-icons/io5';
import {MdEdit, MdOutlineClose} from 'react-icons/md';
import {
  PiUploadSimpleBold,
  PiChatTextBold,
  PiDotsThreeCircleBold,
  PiPencilSimpleLineBold,
  PiCalculatorBold,
  PiMinusBold,
  PiMagnifyingGlassBold,
  PiTrashBold,
  PiImagesBold,
  PiLinkBreakBold,
  PiCaretDown,
  PiPlusBold,
  PiGavelBold,
  PiArrowUUpLeftBold,
  PiClockCounterClockwiseBold,
  PiClockFill,
  PiCaretCircleUpFill,
  PiArrowsInLineVerticalBold,
  PiArrowsClockwiseBold,
  PiPaperPlaneRightBold,
} from 'react-icons/pi';
import {HiPlayCircle} from 'react-icons/hi2';
import {TbAlertTriangleFilled} from 'react-icons/tb';
import {PricePredictionIcon as PricePredictionIconSvg} from '../Assets';

type IconProps = {
  color?: keyof typeof theme.colors | 'inherit';
  size?: keyof typeof theme.font.sizes | 'inherit';
  style?: CSSProperties;
} & IconBaseProps;

export const PricePredictionIcon = () => (
  <img src={PricePredictionIconSvg} style={{width: '16px', height: '16px'}} />
);
export const ChevronArrowDown = (props: IconProps) => iconFactory(FaChevronDown, props);
export const CaretDownIcon = (props: IconProps) => iconFactory(PiCaretDown, props);
export const ChevronArrowRight = (props: IconProps) => iconFactory(FaChevronRight, props);
export const ChevronArrowLeft = (props: IconProps) => iconFactory(FaChevronLeft, props);
export const ChevronArrowUp = (props: IconProps) => iconFactory(FaChevronUp, props);
export const ArrowDownIcon = (props: IconProps) => iconFactory(FiArrowDown, props);
export const ArrowUpIcon = (props: IconProps) => iconFactory(FiArrowUp, props);
export const CircleUpArrowIcon = (props: IconProps) => iconFactory(BiChevronUpCircle, props);
export const CircleDownArrowIcon = (props: IconProps) => iconFactory(BiChevronDownCircle, props);
export const CalendarIcon = (props: IconProps) => iconFactory(FaRegCalendarAlt, props);
export const MenuIcon = (props: IconProps) => iconFactory(BiMenu, props);
export const InfoIcon = (props: IconProps) => iconFactory(AiFillInfoCircle, props);
export const CloseIcon = (props: IconProps) => iconFactory(MdOutlineClose, props);
export const PlayArrowIcon = (props: IconProps) => iconFactory(FaPlay, props);
export const ToolIcon = (props: IconProps) => iconFactory(FiTool, props);
export const WarningIcon = (props: IconProps) => iconFactory(IoWarning, {color: 'red', ...props});
export const CheckIcon = (props: IconProps) => iconFactory(BsCheckLg, {color: 'white', ...props});
export const EditIcon = (props: IconProps) => iconFactory(MdEdit, props);
export const UndoIcon = (props: IconProps) => iconFactory(BiUndo, props);
export const TwitterIcon = (props: IconProps) => iconFactory(FaTwitter, props);
export const InstagramIcon = (props: IconProps) => iconFactory(FaInstagram, props);
export const FacebookIcon = (props: IconProps) => iconFactory(FaFacebookF, props);
export const YelpIcon = (props: IconProps) => iconFactory(FaYelp, props);
export const PinterestIcon = (props: IconProps) => iconFactory(FaPinterest, props);
export const ExternalLinkIcon = (props: IconProps) => iconFactory(FaExternalLinkAlt, props);
export const UploadIcon = (props: IconProps) => iconFactory(PiUploadSimpleBold, props);
export const ChatTextIcon = (props: IconProps) => iconFactory(PiChatTextBold, props);
export const DotsThreeCircleIcon = (props: IconProps) => iconFactory(PiDotsThreeCircleBold, props);
export const PlayCircleIcon = (props: IconProps) => iconFactory(HiPlayCircle, props);
export const PencilSimpleLineIcon = (props: IconProps) =>
  iconFactory(PiPencilSimpleLineBold, props);
export const CalculatorIcon = (props: IconProps) => iconFactory(PiCalculatorBold, props);
export const MinusIcon = (props: IconProps) => iconFactory(PiMinusBold, props);
export const SearchIcon = (props: IconProps) => iconFactory(PiMagnifyingGlassBold, props);
export const CloseOutlineIcon = (props: IconProps) => iconFactory(IoCloseOutline, props);
export const TrashIcon = (props: IconProps) => iconFactory(PiTrashBold, props);
export const ImagesIcon = (props: IconProps) => iconFactory(PiImagesBold, props);
export const LinkBreakIcon = (props: IconProps) => iconFactory(PiLinkBreakBold, props);
export const PlusIcon = (props: IconProps) => iconFactory(PiPlusBold, props);
export const GavelIcon = (props: IconProps) => iconFactory(PiGavelBold, props);
export const RevertIcon = (props: IconProps) => iconFactory(PiArrowUUpLeftBold, props);
export const ClockIcon = (props: IconProps) => iconFactory(PiClockCounterClockwiseBold, props);
export const ClockFillIcon = (props: IconProps) => iconFactory(PiClockFill, props);
export const CaretCircleUpIcon = (props: IconProps) => iconFactory(PiCaretCircleUpFill, props);
export const AlertIcon = (props: IconProps) => iconFactory(TbAlertTriangleFilled, props);
export const CollapseIcon = (props: IconProps) => iconFactory(PiArrowsInLineVerticalBold, props);
export const RefreshIcon = (props: IconProps) => iconFactory(PiArrowsClockwiseBold, props);
export const SendIcon = (props: IconProps) => iconFactory(PiPaperPlaneRightBold, props);

const iconFactory = (
  Icon: IconType,
  {size = 'medium', color = 'darkBlue', style, ...rest}: IconBaseProps,
): JSX.Element => (
  <Icon
    style={{fontSize: theme.font.sizes[size], color: theme.colors[color], ...style}}
    {...rest}
  />
);
